import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Linkedin, Facebook, ChevronRight } from "lucide-react";
import { openConsentSettings } from "@/lib/consent";

import { getTranslation } from "@/lib/translations";
import { getPageTranslation } from "@/lib/translations";
import { defaultLocale, getLocaleFromPathname, type Locale } from "@/lib/i18n";
import { EmailLink } from "@/components/email-link";

interface FooterProps {
  locale?: Locale;
}

export function Footer({ locale = defaultLocale }: FooterProps) {
  const location = useLocation();
  const currentLocale = getLocaleFromPathname(location.pathname);
  const t = getTranslation(currentLocale);
  const pt = getPageTranslation(currentLocale);
  const [hqOpen, setHqOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);

  const getLocalizedPath = (path: string, targetLocale: Locale) => {
    if (targetLocale === defaultLocale) {
      return path;
    }
    return `/${targetLocale}${path}`;
  };

  const footerLinks = [
    { key: "imprint", href: "/imprint" },
    { key: "privacy", href: "/privacy" },
    { key: "howToGetHere", href: `/${t.routes.about}/${t.routes.anfahrt}` },
    { key: "documents", href: `/${t.routes.documents}` },
  ];

  return (
    <footer className="bg-white dark:bg-background border-t border-border mt-20">
      <div className="container mx-auto px-4 py-10">
        {/* Single Row Layout */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-16">

          {/* Hauptsitz */}
          <div className="flex-shrink-0">
            <button
              onClick={() => setHqOpen(!hqOpen)}
              className="md:hidden flex items-center gap-1.5 font-normal tracking-wide text-foreground mb-2"
            >
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${hqOpen ? 'rotate-90' : ''}`} />
              {pt.footer.headquarters}
            </button>
            <h3 className="hidden md:block font-normal tracking-wide text-foreground mb-2">{pt.footer.headquarters}</h3>
            <div className={`text-sm text-muted-foreground ${hqOpen ? 'block' : 'hidden'} md:block`}>
              <p>W. Althaus AG</p>
              <p>Industrielle Automation</p>
              <p>Jurastrasse 12</p>
              <p>CH-4912 Aarwangen</p>
              <p>Switzerland</p>
            </div>
          </div>

          {/* Produktion */}
          <div className="flex-shrink-0">
            <button
              onClick={() => setProdOpen(!prodOpen)}
              className="md:hidden flex items-center gap-1.5 font-normal tracking-wide text-foreground mb-2"
            >
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${prodOpen ? 'rotate-90' : ''}`} />
              {pt.footer.production}
            </button>
            <h3 className="hidden md:block font-normal tracking-wide text-foreground mb-2">{pt.footer.production}</h3>
            <div className={`text-sm text-muted-foreground ${prodOpen ? 'block' : 'hidden'} md:block`}>
              <p>W. Althaus AG</p>
              <p>Industrielle Automation</p>
              <p>Industriering 2</p>
              <p>CH-4912 Aarwangen</p>
              <p>Switzerland</p>
            </div>
          </div>

          {/* Kontakt */}
          <div className="flex-shrink-0">
            {/* Mobile: Dropdown */}
            <button
              onClick={() => setContactOpen(!contactOpen)}
              className="md:hidden flex items-center gap-1.5 font-normal tracking-wide text-foreground mb-2"
            >
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${contactOpen ? 'rotate-90' : ''}`} />
              {pt.footer.contact}
            </button>
            {/* Desktop: Static title */}
            <h3 className="hidden md:block font-normal tracking-wide text-foreground mb-2">{pt.footer.contact}</h3>
            {/* Content: always visible on desktop, toggle on mobile */}
            <div className={`text-sm text-muted-foreground ${contactOpen ? 'block' : 'hidden'} md:block`}>
              <p>{pt.footer.phone} +41 62 919 82 10</p>
              <p><EmailLink mailKey="info" locale={currentLocale} className="hover:underline" /></p>
            </div>
          </div>

          {/* Öffnungszeiten */}
          <div className="flex-shrink-0">
            {/* Mobile: Dropdown */}
            <button
              onClick={() => setHoursOpen(!hoursOpen)}
              className="md:hidden flex items-center gap-1.5 font-normal tracking-wide text-foreground mb-2"
            >
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${hoursOpen ? 'rotate-90' : ''}`} />
              {t.company.hours}
            </button>
            {/* Desktop: Static title */}
            <h3 className="hidden md:block font-normal tracking-wide text-foreground mb-2">{t.company.hours}</h3>
            {/* Content */}
            <div className={`text-sm text-muted-foreground ${hoursOpen ? 'block' : 'hidden'} md:block`}>
              <p className="font-medium text-foreground/80">{pt.footer.openingHours.weekdays}</p>
              <p>07.30 - 12.00 Uhr / 13.00 - 16.00 Uhr</p>
              <p className="font-medium text-foreground/80 mt-2">{pt.footer.openingHours.friday}</p>
              <p>07.30 - 12.00 Uhr / 13.00 - 15.00 Uhr</p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex-shrink-0">
            <h3 className="font-normal tracking-wide text-foreground mb-2">{t.footer.quickLinks}</h3>
            <ul className="space-y-1">
              {footerLinks.map((link) => (
                <li key={link.key}>
                  <Link
                    to={getLocalizedPath(link.href, currentLocale)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t.footer[link.key as keyof typeof t.footer]}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={openConsentSettings}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
                >
                  {(t as any).cookieConsent?.cookieSettings || 'Cookie-Einstellungen'}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-6 mt-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-sm text-muted-foreground gap-4">
            <p>{t.footer.copyright}</p>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-4">
              <a 
                href="https://ch.linkedin.com/company/w-althaus-ag" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/w.althausag/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.facebook.com/walthausag/"
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
            
            <p>{t.footer.quality}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}